import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useRequest } from '@common/services/api';
import { AlertService } from '@common/services/alert';

import type { CustomUserPropertiesResponse, CustomUserProperty } from '../types';
import {
  buildCustomUserPropertyPredicateFilters,
  type CustomUserPropertyPredicateFilter,
} from '../utils/build-custom-user-property-predicate-filters';
import { useCustomUserPropertiesContextOptional }
from '../contexts/custom-user-properties-context';

const EMPTY_PROPERTIES: CustomUserProperty[] = [];
const EMPTY_PROPERTIES_BY_ID = new Map<string, CustomUserProperty>();
const EMPTY_FILTERS_MAP: Record<string, CustomUserPropertyPredicateFilter> = {};

function getEmptyUserPropertyPredicateFilters() {
  return {
    properties: EMPTY_PROPERTIES,
    propertiesById: EMPTY_PROPERTIES_BY_ID,
    filtersMap: EMPTY_FILTERS_MAP,
    loading: false,
    error: null as Error | null,
  };
}

export function useOrganisationUserPropertyPredicateFilters(
  organisationId: string | undefined,
  enabled = true
) {
  const { t } = useTranslation();
  const shared = useCustomUserPropertiesContextOptional();

  const endpoint = useMemo(
    () => (organisationId ? `/v1/organisations/${organisationId}/user-properties` : ''),
    [organisationId]
  );

  const handleLoadError = useCallback(() => {
    AlertService.error(t('organisation:user_properties_load_error'));
  }, [t]);

  const shouldUseSharedContext = !!(
    enabled
    && shared
    && shared.organisationId === organisationId
  );

  const {
    data: properties = [],
    loading,
    error,
    reset,
  } = useRequest<CustomUserPropertiesResponse>(endpoint, {
    enabled: enabled && !!organisationId && !shouldUseSharedContext,
    onError: handleLoadError,
  });

  const previousOrganisationIdRef = useRef<string | undefined>();

  useEffect(() => {
    if (!enabled || !organisationId || shouldUseSharedContext) return;

    const previousOrganisationId = previousOrganisationIdRef.current;
    previousOrganisationIdRef.current = organisationId;

    // Refetch only when switching organisations. Skip on initial mount — useRequest
    // handles that. Do not list `reset` in deps: useRequest's reset identity changes
    // every render, which would cause an infinite update loop.
    if (previousOrganisationId && previousOrganisationId !== organisationId) {
      reset();
    }
    // eslint-disable-next-line -- reset identity is unstable in useRequest
  }, [enabled, organisationId, shouldUseSharedContext]);

  const localFiltersMap = useMemo(
    () => buildCustomUserPropertyPredicateFilters(properties),
    [properties]
  );

  const localPropertiesById = useMemo(
    () => new Map(properties.map((property) => [property.id, property])),
    [properties]
  );

  if (!enabled) {
    return getEmptyUserPropertyPredicateFilters();
  }

  if (shouldUseSharedContext && shared) {
    return {
      properties: shared.properties,
      propertiesById: shared.propertiesById,
      filtersMap: shared.filtersMap,
      loading: shared.loading,
      error: shared.error,
    };
  }

  return {
    properties,
    propertiesById: localPropertiesById,
    filtersMap: localFiltersMap,
    loading,
    error: (error as Error | null) ?? null,
  };
}
